/* @generated */

// ../src/hooks/useUniqueId.ts
import * as React from "react";
var s4 = () => Math.floor((1 + Math.random()) * 65536).toString(16).substring(1);
var makeUuid = () => s4() + s4() + "-" + s4() + "-" + s4() + "-" + s4() + "-" + s4() + s4() + s4();
function useUniqueId({ prefix, idOverride }) {
  const [id, setId] = React.useState(idOverride || "");
  React.useEffect(() => {
    if (!id) {
      setId(`${prefix}${makeUuid()}`);
    }
  }, [prefix, id]);
  return idOverride || id;
}

// ../src/hooks/useMaxHeight.ts
import { useIsomorphicLayoutEffect } from "@react-hookz/web";
import * as React2 from "react";
var DEFAULT_MAX_HEIGHT = 200;
var useMaxHeight = ({
  hasMaxHeight,
  contentRef,
  style
}) => {
  const [isTransitionDone, setIsTransitionDone] = React2.useState(false);
  const transitionDuration = React2.useRef(
    style && style.transitionDuration ? parseInt(style.transitionDuration, 10) : 0
  );
  const getStyles = React2.useCallback(() => {
    let maxHeight = typeof hasMaxHeight === "number" ? hasMaxHeight : hasMaxHeight === true ? DEFAULT_MAX_HEIGHT : "none";
    if (contentRef && contentRef.current && isTransitionDone) {
      const { top } = contentRef.current.getBoundingClientRect();
      const explicitMax = hasMaxHeight === true ? DEFAULT_MAX_HEIGHT : hasMaxHeight;
      const combinedMax = explicitMax ? explicitMax + top : 0;
      maxHeight = !!explicitMax && combinedMax > 0 && combinedMax <= window.innerHeight ? explicitMax : Math.floor((window.innerHeight - top) * 0.9);
      if (maxHeight <= 0 && !!explicitMax) {
        maxHeight = explicitMax;
      }
    }
    return { maxHeight: maxHeight > 0 ? maxHeight : "none" };
  }, [contentRef, hasMaxHeight, isTransitionDone]);
  const [styles, setStyles] = React2.useState(getStyles());
  useIsomorphicLayoutEffect(() => {
    const id = setTimeout(() => setIsTransitionDone(true), transitionDuration.current);
    return () => {
      window.clearTimeout(id);
    };
  }, [transitionDuration]);
  useIsomorphicLayoutEffect(() => {
    setStyles(getStyles());
  }, [getStyles]);
  return { styles };
};

// ../src/hooks/useMenu/useMenu.ts
import * as React5 from "react";

// ../src/_global/constants.ts
var ensureFocusable = (selector) => {
  return `${selector}:not([tabindex="-1"])`;
};
var TAB_KEY = "Tab";
var ENTER_KEY = "Enter";
var ESC_KEY = "Escape";
var SPACEBAR_KEY = " ";
var UP_ARROW_KEY = "ArrowUp";
var DOWN_ARROW_KEY = "ArrowDown";
var LEFT_ARROW_KEY = "ArrowLeft";
var RIGHT_ARROW_KEY = "ArrowRight";
var NONE_SELECTED_INDEX = -2;
var LAST_SELECTED_INDEX = -1;
var FOCUSABLE_SELECTOR = (additionalSelectors) => Array.prototype.concat.apply(
  [],
  [
    // buttons and links
    [`button`, `[role="button"]`, `[role="link"]`, `[href]`].map(ensureFocusable),
    // controls
    [`select`, `textarea`, `[role="textbox"]`].map(ensureFocusable),
    additionalSelectors,
    // other
    [`[tabindex]:not([tabindex="-1"])`]
  ]
).join(",");
var buttonDataAttributeName = "data-dig-button";
var buttonDataSelector = `[${buttonDataAttributeName}]`;
var buttonContentDataAttributeName = "data-dig-button-content";
var buttonContentDataSelector = `[${buttonContentDataAttributeName}]`;
var splitButtonDataAttributeName = "data-dig-split-button";
var splitButtonDataSelector = `[${splitButtonDataAttributeName}]`;
var iconButtonDataAttributeName = "data-dig-icon-button";
var iconButtonDataSelector = `[${iconButtonDataAttributeName}]`;

// ../src/hooks/useMenu/useMenuRows.ts
import * as React3 from "react";
var sortByIndex = (a, b) => {
  if (a[1].index !== void 0 && b[1].index !== void 0) {
    return a[1].index - b[1].index;
  } else {
    return -1;
  }
};
var reducer = (state, action) => {
  let changes;
  switch (action.type) {
    case "UPDATE_ROW":
      const updateRows = new Map(state.menuRows);
      updateRows.set(action.payload.key, action.payload.value);
      changes = action.payload.value.index !== void 0 ? { menuRows: new Map(Array.from(updateRows.entries()).sort(sortByIndex)) } : { menuRows: updateRows };
      break;
    case "REMOVE_ROW":
      const removeRows = new Map(state.menuRows);
      removeRows.delete(action.payload.key);
      changes = { menuRows: removeRows };
      break;
    case "SET_SELECTED_INDEX":
      changes = { selectedIndex: action.payload.index };
      break;
    case "SET_FOCUS_INDEX":
      changes = { focusIndex: action.payload.index };
      break;
  }
  return { ...state, ...changes };
};
var initialState = {
  menuRows: /* @__PURE__ */ new Map(),
  selectedIndex: NONE_SELECTED_INDEX,
  focusIndex: NONE_SELECTED_INDEX
};
function isRowFirst(menuRows, index) {
  return menuRows.items.size > 0 && menuRows.items.values().next().value.index === index;
}
function areRowsFocused(menuRows) {
  return menuRows.focusIndex !== NONE_SELECTED_INDEX;
}
var createMenuRows = () => ({
  items: /* @__PURE__ */ new Map(),
  selectedIndex: NONE_SELECTED_INDEX,
  setSelectedIndex: () => false,
  addRow: () => false,
  removeRow: () => false,
  updateRow: () => false,
  shouldFocusDisabledRows: true,
  focusIndex: NONE_SELECTED_INDEX,
  setFocusIndex: () => false
});
var useMenuRows = (options = {}) => {
  const { shouldFocusDisabledRows = true } = options;
  const [state, dispatch] = React3.useReducer(reducer, initialState);
  const setSelectedIndex = (index) => {
    dispatch({ type: "SET_SELECTED_INDEX", payload: { index } });
  };
  const setFocusIndex = (index) => {
    dispatch({ type: "SET_FOCUS_INDEX", payload: { index } });
  };
  const updateRow = ({ disabled, focusable, ref, index, itemValue }) => {
    if (ref) {
      dispatch({
        type: "UPDATE_ROW",
        payload: {
          key: ref,
          value: {
            textContent: ref.textContent || "",
            disabled: disabled || false,
            focusable: focusable || false,
            index,
            itemValue
          }
        }
      });
    }
  };
  const addRow = updateRow;
  const removeRow = (ref) => {
    if (ref) {
      dispatch({
        type: "REMOVE_ROW",
        payload: { key: ref }
      });
    }
  };
  return {
    selectedIndex: state.selectedIndex,
    setSelectedIndex,
    items: state.menuRows,
    addRow,
    removeRow,
    updateRow,
    shouldFocusDisabledRows,
    focusIndex: state.focusIndex,
    setFocusIndex
  };
};

// ../src/hooks/useMenu/utils.ts
var getFocusableRows = (rows) => {
  const focusableRows = /* @__PURE__ */ new Map();
  rows.forEach((row, ref) => {
    if (row.focusable) {
      focusableRows.set(ref, row);
    }
  });
  return focusableRows;
};
var getPreviousFocusableItem = (rows, startIndex, firstFound = false) => {
  let prevIndex = NONE_SELECTED_INDEX;
  let foundCurrent = false;
  const refs = Array.from(rows.keys());
  for (let i = refs.length - 1; i >= 0; i--) {
    const ref = refs[i];
    if (foundCurrent) {
      if (rows.has(ref) && rows.get(ref).focusable) {
        prevIndex = i;
        break;
      }
    } else {
      if (i === startIndex) {
        foundCurrent = true;
        if (firstFound) {
          return rows.get(ref).focusable ? (
            // Return it if it is focusable
            i
          ) : (
            // Otherwise, recursively find the next one
            getNextFocusableItem(rows, i - 1, true)
          );
        }
      }
    }
  }
  return prevIndex;
};
var getNextFocusableItem = (rows, startIndex, firstFound = false) => {
  let nextIndex = NONE_SELECTED_INDEX;
  let foundCurrent = false;
  const refs = Array.from(rows.keys());
  for (let i = 0; i < refs.length; i++) {
    const ref = refs[i];
    if (foundCurrent) {
      if (rows.has(ref) && rows.get(ref).focusable) {
        nextIndex = i;
        break;
      }
    } else {
      if (i === startIndex) {
        foundCurrent = true;
        if (firstFound) {
          return rows.get(ref).focusable ? (
            // Return it if it is focusable
            i
          ) : (
            // Otherwise, recursively find the next one
            getNextFocusableItem(rows, i + 1, true)
          );
        }
      }
    }
  }
  return nextIndex;
};
var moveFocusTo = (rows, currentIndex) => {
  if (currentIndex < rows.size) {
    const newIndex = currentIndex === LAST_SELECTED_INDEX ? (
      // If the index is `LAST_SELECTED_INDEX`, move the focus to the last element
      rows.size - 1
    ) : currentIndex;
    return newIndex;
  }
  return currentIndex;
};
var moveFocusBackward = (rows, currentIndex) => {
  let prevIndex = getPreviousFocusableItem(rows, currentIndex);
  if (prevIndex === NONE_SELECTED_INDEX) {
    prevIndex = getPreviousFocusableItem(rows, rows.size - 1, true);
  }
  return moveFocusTo(rows, prevIndex);
};
var moveFocusForward = (rows, currentIndex) => {
  let nextIndex = getNextFocusableItem(rows, currentIndex);
  if (nextIndex === NONE_SELECTED_INDEX) {
    nextIndex = getNextFocusableItem(rows, 0, true);
  }
  return moveFocusTo(rows, nextIndex);
};
var moveFocusByString = (rows, letter, currentIndex, startIndex = currentIndex, firstFound = false) => {
  let newIndex = NONE_SELECTED_INDEX;
  const letterDown = letter.toLowerCase();
  const rowsValues = Array.from(rows.values());
  for (let i = startIndex; i < rowsValues.length; i++) {
    if (!rowsValues[i].focusable) continue;
    if (!rowsValues[i].textContent.toLowerCase().startsWith(letterDown)) continue;
    if (firstFound) return i;
    if (i !== startIndex) {
      newIndex = i;
      break;
    }
  }
  if (newIndex === NONE_SELECTED_INDEX) {
    if (firstFound) {
      return currentIndex;
    }
    newIndex = moveFocusByString(rows, letter, currentIndex, 0, true);
  }
  return moveFocusTo(rows, newIndex);
};
var isFocusingMenuRow = (nodes, target) => {
  return nodes.has(target);
};
var isMenuRowDisabled = (nodes, target) => {
  let disabled = false;
  nodes.forEach((node, key) => {
    if (key === target) {
      disabled = node.disabled;
    }
  });
  return disabled;
};
var returnFocus = (ref, delayFocus) => {
  const _returnFocus = () => {
    if (ref.current) {
      if ("focus" in ref.current) {
        ref.current.focus({ preventScroll: true });
      }
    }
  };
  if (typeof delayFocus === "number") {
    setTimeout(() => {
      _returnFocus();
    }, delayFocus);
  } else {
    _returnFocus();
  }
};
var focusItem = (menuItems, index, delayFocus = 0) => {
  const _focusItem = () => {
    const newFocus = moveFocusTo(menuItems, index);
    const rowsArr = Array.from(menuItems.keys());
    const row = rowsArr[newFocus];
    if (row && "focus" in row) row.focus();
  };
  setTimeout(() => {
    _focusItem();
  }, delayFocus);
};
var isKeyboardEvent = (evt) => {
  switch (evt.type) {
    case "keydown":
    case "keypress":
    case "keyup":
      return true;
    default:
      return false;
  }
};
var isAnchorElement = (el) => {
  if (!el) return false;
  return el.tagName === "A";
};
var isButtonElement = (el) => {
  if (!el) return false;
  return el.tagName === "BUTTON";
};

// ../src/utils.ts
import * as React4 from "react";
var useDidUpdateEffect = (fn, deps) => {
  const didMountRef = React4.useRef(false);
  React4.useEffect(() => {
    if (didMountRef.current) fn();
    else didMountRef.current = true;
  }, deps);
};

// ../src/hooks/useMenu/useMenu.ts
var initialState2 = {
  open: false,
  value: "",
  performCloseMenu: false,
  triggeredByKey: false,
  shouldTriggerClick: true,
  autoFocusItem: NONE_SELECTED_INDEX,
  isFocusing: false
};
function reducer2(state, action) {
  let changes;
  switch (action.type) {
    case "OPEN":
      changes = {
        open: action.payload.open
      };
      break;
    case "CLOSE":
      changes = {
        open: false,
        // Ensure we re-enable the `shouldTriggerClick` flag so new clicks can work
        shouldTriggerClick: true,
        value: void 0
      };
      break;
    case "DEFER_CLOSE":
      changes = {
        performCloseMenu: action.payload.performCloseMenu
      };
      break;
    case "RESET_DEFER_CLOSE_MENU":
      changes = {
        performCloseMenu: false
      };
      break;
    case "SET_VALUE":
      changes = { value: action.payload.value };
      break;
    case "SET_SHOULD_TRIGGER_CLICK":
      changes = { shouldTriggerClick: action.payload.value };
      break;
    case "RESET_SHOULD_TRIGGER_CLICK":
      changes = { shouldTriggerClick: true };
      break;
    case "SET_TRIGGERED_BY_KEY":
      changes = { triggeredByKey: action.payload.value };
      break;
    case "RESET_TRIGGERED_BY_KEY":
      changes = { triggeredByKey: false };
      break;
    case "AUTOFOCUS_ITEM":
      changes = { autoFocusItem: action.payload.index };
      break;
    case "RESET_AUTOFOCUS_ITEM":
      changes = { autoFocusItem: NONE_SELECTED_INDEX };
      break;
    case "SET_IS_FOCUSING":
      changes = { isFocusing: action.payload.isFocusing };
      break;
  }
  return { ...state, ...changes };
}
var useMenu = ({
  triggerRef,
  contentRef,
  onSelection,
  onToggle,
  closeOnSelection,
  shouldFocusDisabledRows = true,
  allowLetterNavigation = true,
  allowSpaceBarToOpenMenu = true,
  focusNextItemImmediately = true,
  focusMenuRowTriggerKey = TAB_KEY,
  interactiveRowSelectors,
  delayFocus = void 0,
  shouldHighlightFirstRow,
  isContentLabelledByTrigger
}) => {
  const [state, dispatch] = React5.useReducer(reducer2, initialState2);
  const menuRows = useMenuRows({ shouldFocusDisabledRows });
  const uniqueTriggerId = useUniqueId({ prefix: "menu" });
  const openMenu = ({ autoFocus = true, index = 0 }) => {
    dispatch({ type: "OPEN", payload: { open: { autoFocus, index } } });
  };
  const closeMenu = React5.useCallback(
    ({ autoFocus = false, ref, immediate }) => {
      dispatch({ type: "CLOSE" });
      if (autoFocus && ref) {
        returnFocus(ref, immediate === true ? void 0 : delayFocus);
      }
    },
    [delayFocus]
  );
  const toggleMenu = (options) => {
    const autoFocus = options && options.autoFocus;
    if (state.open) {
      closeMenu({ autoFocus, ref: triggerRef });
    } else {
      openMenu({ autoFocus });
    }
  };
  const setSelected = (value, event, options = { stayOpen: false }) => {
    if (isMenuRowDisabled(menuRows.items, event.target)) {
      return;
    }
    const stayOpen = options && options.stayOpen;
    if (closeOnSelection && !stayOpen) {
      const autoFocus = isKeyboardEvent(event);
      dispatch({
        type: "DEFER_CLOSE",
        payload: { performCloseMenu: { autoFocus, ref: triggerRef, immediate: true } }
      });
    }
    if (onSelection) {
      onSelection(value, event);
    }
  };
  const setFocused = (value, index) => {
    dispatch({ type: "SET_VALUE", payload: { value } });
    dispatch({ type: "SET_IS_FOCUSING", payload: { isFocusing: false } });
    if (index) {
      menuRows.setFocusIndex(index);
    }
  };
  const setSyntheticFocus = () => {
    dispatch({ type: "SET_IS_FOCUSING", payload: { isFocusing: true } });
    setTimeout(() => {
      dispatch({ type: "SET_IS_FOCUSING", payload: { isFocusing: false } });
    }, 0);
  };
  const handleTriggerKeyDown = (evt) => {
    if (state.open) {
      if (contentRef && contentRef.current && evt.key === focusMenuRowTriggerKey) {
        const focusable = contentRef.current.querySelector(
          FOCUSABLE_SELECTOR(interactiveRowSelectors)
        );
        if (focusable) {
          dispatch({ type: "SET_IS_FOCUSING", payload: { isFocusing: true } });
          setTimeout(() => {
            if (focusable) focusable.focus();
          }, 0);
        }
      }
      return;
    }
    switch (evt.key) {
      case DOWN_ARROW_KEY:
        evt.preventDefault();
        openMenu({ index: 0 });
        break;
      case UP_ARROW_KEY:
        evt.preventDefault();
        openMenu({ index: LAST_SELECTED_INDEX });
        break;
      case SPACEBAR_KEY:
        if (allowSpaceBarToOpenMenu) evt.preventDefault();
        break;
    }
  };
  const handleTriggerKeyUp = (evt) => {
    if (allowSpaceBarToOpenMenu && evt.key === SPACEBAR_KEY) {
      dispatch({ type: "SET_SHOULD_TRIGGER_CLICK", payload: { value: false } });
      openMenu({ autoFocus: true });
    }
  };
  useDidUpdateEffect(() => {
    if (state.performCloseMenu) {
      closeMenu(state.performCloseMenu);
      dispatch({ type: "RESET_DEFER_CLOSE_MENU" });
    }
  }, [state.performCloseMenu]);
  useDidUpdateEffect(() => {
    if (!state.open) {
      if (onToggle) {
        onToggle({ isOpen: Boolean(state.open) });
      }
      return;
    }
    if (state.open.autoFocus) {
      dispatch({
        type: "AUTOFOCUS_ITEM",
        payload: { index: state.open.index }
      });
    } else {
      const setContentFocus = () => {
        if (contentRef.current) {
          contentRef.current.focus({ preventScroll: true });
          menuRows.setSelectedIndex(NONE_SELECTED_INDEX);
          menuRows.setFocusIndex(NONE_SELECTED_INDEX);
        }
      };
      if (typeof delayFocus === "number") {
        setTimeout(() => {
          setContentFocus();
        }, delayFocus);
      } else {
        setContentFocus();
      }
    }
    if (onToggle) {
      onToggle({ isOpen: Boolean(state.open) });
    }
  }, [state.open]);
  const handleKeyUp = (evt) => {
    if (!state.open) return;
    const target = evt.target;
    if (evt.key === SPACEBAR_KEY && isFocusingMenuRow(menuRows.items, target)) {
      if (isAnchorElement(target)) {
        window.open(target.href, target.target || "_self");
        return;
      } else {
        evt.preventDefault();
      }
      setSelected(state.value, evt);
    }
  };
  const handleKeyDown = (evt) => {
    if (!state.open) return;
    const target = evt.target;
    let runLetterNavigation = allowLetterNavigation;
    switch (evt.key) {
      case TAB_KEY:
        if (!contentRef || !contentRef.current) return;
        const focusable = contentRef.current.querySelectorAll(
          FOCUSABLE_SELECTOR(interactiveRowSelectors)
        );
        if (
          // If we're currently focused on an interactive `Menu.Row`
          isFocusingMenuRow(menuRows.items, target) && // we only want close the menu where there are no custom elements within the same menu
          // to ensure users can continue tabbing through to the custom elements
          focusable.length <= menuRows.items.size
        ) {
          evt.preventDefault();
          closeMenu({ autoFocus: true, ref: triggerRef });
          return;
        }
        runLetterNavigation = false;
        if (evt.shiftKey) {
          if (focusable.length === 0) {
            closeMenu({ autoFocus: true, ref: triggerRef });
          }
          if (document && document.activeElement === focusable[0]) {
            evt.preventDefault();
            closeMenu({ autoFocus: true, ref: triggerRef });
          }
        } else {
          if (focusable.length === 0) {
            closeMenu({ autoFocus: true, ref: triggerRef });
          }
          if (document && Array.from(focusable).indexOf(document.activeElement) !== -1) {
            evt.preventDefault();
            closeMenu({ autoFocus: true, ref: triggerRef });
          }
        }
        break;
      case ESC_KEY:
        runLetterNavigation = false;
        evt.preventDefault();
        closeMenu({ autoFocus: true, ref: triggerRef, immediate: true });
        break;
      case DOWN_ARROW_KEY:
        runLetterNavigation = false;
        evt.preventDefault();
        evt.stopPropagation();
        dispatch({ type: "SET_IS_FOCUSING", payload: { isFocusing: true } });
        const focusNextItem = () => {
          const nextIndex = menuRows.selectedIndex === NONE_SELECTED_INDEX ? (
            // If nothing is selected, shift focus to first item
            moveFocusTo(menuRows.items, 0)
          ) : (
            // Otherwise, shift focus to the next available (loops when necessary)
            moveFocusForward(menuRows.items, menuRows.selectedIndex)
          );
          focusItem(menuRows.items, nextIndex);
          menuRows.setSelectedIndex(nextIndex);
          menuRows.setFocusIndex(nextIndex);
        };
        if (focusNextItemImmediately) {
          focusNextItem();
        } else {
          setTimeout(() => {
            focusNextItem();
          }, 0);
        }
        break;
      case UP_ARROW_KEY:
        runLetterNavigation = false;
        evt.preventDefault();
        evt.stopPropagation();
        dispatch({ type: "SET_IS_FOCUSING", payload: { isFocusing: true } });
        const focusPreviousItem = () => {
          const prevIndex = menuRows.selectedIndex === NONE_SELECTED_INDEX ? (
            // If nothing is selected, shift focus to last item
            moveFocusTo(menuRows.items, LAST_SELECTED_INDEX)
          ) : (
            // Otherwise, shift focus to the previous available (loops when necessary)
            moveFocusBackward(menuRows.items, menuRows.selectedIndex)
          );
          focusItem(menuRows.items, prevIndex);
          menuRows.setSelectedIndex(prevIndex);
          menuRows.setFocusIndex(prevIndex);
        };
        if (focusNextItemImmediately) {
          focusPreviousItem();
        } else {
          setTimeout(() => {
            focusPreviousItem();
          }, 0);
        }
        break;
      case LEFT_ARROW_KEY:
      case RIGHT_ARROW_KEY:
        if (isFocusingMenuRow(menuRows.items, target)) {
          runLetterNavigation = false;
          evt.preventDefault();
          evt.stopPropagation();
        }
        break;
      case SPACEBAR_KEY:
        runLetterNavigation = false;
        if (isFocusingMenuRow(menuRows.items, target)) {
          evt.preventDefault();
          evt.stopPropagation();
        }
        break;
      case ENTER_KEY:
        runLetterNavigation = false;
        if (isFocusingMenuRow(menuRows.items, target)) {
          if (!isAnchorElement(target) && !isButtonElement(target)) {
            evt.preventDefault();
          }
          evt.stopPropagation();
          if (isAnchorElement(target)) {
            return;
          }
          setSelected(state.value, evt);
        } else {
          if (shouldHighlightFirstRow) {
            if (menuRows.items.values().next().value && typeof menuRows.items.values().next().value === "object") {
              setSelected(menuRows.items.values().next().value.itemValue, evt);
            }
          }
        }
        break;
    }
    if (runLetterNavigation) {
      const newIndex = moveFocusByString(
        menuRows.items,
        evt.key,
        Math.max(menuRows.selectedIndex, 0)
      );
      focusItem(menuRows.items, newIndex);
      menuRows.setSelectedIndex(newIndex);
      menuRows.setFocusIndex(newIndex);
    }
  };
  const focusTrigger = React5.useCallback(() => {
    returnFocus(triggerRef);
  }, [triggerRef]);
  const getRootProps = (options = {}) => {
    const { onKeyDown, onKeyUp, ...rest } = options;
    return {
      onKeyDown: (evt) => {
        handleKeyDown(evt);
        if (onKeyDown) onKeyDown(evt);
      },
      onKeyUp: (evt) => {
        handleKeyUp(evt);
        if (onKeyUp) onKeyUp(evt);
      },
      ...rest
    };
  };
  const getTriggerProps = (options = {}) => {
    const { onMouseDown, onClick, onKeyDown, onKeyUp, ...rest } = options;
    const triggerProps = {
      ref: triggerRef,
      "aria-haspopup": true,
      "aria-expanded": Boolean(state.open),
      onMouseDown: (evt) => {
        dispatch({ type: "RESET_TRIGGERED_BY_KEY" });
        if (onMouseDown) {
          onMouseDown(evt);
        }
      },
      onClick: (evt) => {
        if (!state.shouldTriggerClick) {
          return false;
        }
        const autoFocus = state.triggeredByKey ? true : false;
        toggleMenu({ autoFocus });
        dispatch({ type: "RESET_TRIGGERED_BY_KEY" });
        dispatch({ type: "RESET_SHOULD_TRIGGER_CLICK" });
        if (onClick) {
          onClick(evt);
        }
      },
      onKeyDown: (evt) => {
        dispatch({ type: "SET_TRIGGERED_BY_KEY", payload: { value: true } });
        handleTriggerKeyDown(evt);
        if (onKeyDown) {
          onKeyDown(evt);
        }
      },
      onKeyUp: (evt) => {
        handleTriggerKeyUp(evt);
        if (onKeyUp) {
          onKeyUp(evt);
        }
      },
      ...rest
    };
    if (isContentLabelledByTrigger === true) {
      triggerProps["id"] = uniqueTriggerId;
    }
    return triggerProps;
  };
  const getContentProps = (options = {}) => {
    const contentProps = {
      role: "menu",
      tabIndex: options.tabIndex || -1,
      ...options
    };
    if (isContentLabelledByTrigger === true) {
      contentProps["aria-labelledby"] = uniqueTriggerId;
    }
    return contentProps;
  };
  useDidUpdateEffect(() => {
    if (state.autoFocusItem !== NONE_SELECTED_INDEX) {
      focusItem(menuRows.items, state.autoFocusItem, delayFocus);
      menuRows.setSelectedIndex(state.autoFocusItem);
      menuRows.setFocusIndex(state.autoFocusItem);
      dispatch({ type: "RESET_AUTOFOCUS_ITEM" });
    }
  }, [state.autoFocusItem]);
  return {
    isOpen: state.open,
    openMenu,
    closeMenu,
    toggleMenu,
    value: state.value,
    setSelected,
    setFocused,
    setSyntheticFocus,
    getRootProps,
    getTriggerProps,
    getContentProps,
    menuRows,
    focusTrigger,
    isFocusing: state.isFocusing
  };
};

// ../src/hooks/useMenu/useMenuItem.ts
import * as React6 from "react";
var useMenuItem = ({ interactive, disabled, ref, role = "menuitem", index, itemValue }, menuRows) => {
  const isFocusable = !disabled || menuRows.shouldFocusDisabledRows;
  const update = () => {
    if (!interactive) return;
    menuRows.updateRow({
      disabled,
      focusable: isFocusable,
      ref: ref.current,
      index,
      itemValue
    });
  };
  const getItemProps = (options = {}) => {
    const { tabIndex = -1, ...rest } = options;
    return {
      role,
      tabIndex,
      "aria-disabled": disabled,
      ...rest
    };
  };
  React6.useEffect(() => {
    if (!interactive) return;
    const instance = ref.current;
    menuRows.addRow({
      disabled,
      focusable: isFocusable,
      ref: instance,
      index,
      itemValue
    });
    return () => {
      menuRows.removeRow(instance);
    };
  }, []);
  React6.useEffect(() => update(), [disabled]);
  return { getItemProps };
};

// ../src/hooks/useMenu/useSubmenu.ts
import * as React7 from "react";
var initialState3 = {};
function reducer3(state, action) {
  let changes;
  switch (action.type) {
    case "CLOSE":
      changes = {
        activeSubmenu: void 0
      };
      break;
    case "REGISTER":
      changes = { activeSubmenu: action.payload.activeSubmenu };
      break;
  }
  return { ...state, ...changes };
}
var useSubmenu = () => {
  const [state, dispatch] = React7.useReducer(reducer3, initialState3);
  const register = React7.useCallback(
    (closeFn) => {
      dispatch({ type: "REGISTER", payload: { activeSubmenu: { close: closeFn } } });
    },
    [dispatch]
  );
  const close = React7.useCallback(
    (options) => {
      if (state.activeSubmenu) {
        state.activeSubmenu.close(options);
        dispatch({ type: "CLOSE" });
      }
    },
    [state.activeSubmenu]
  );
  return { register, close, hasActiveSubmenu: Boolean(state.activeSubmenu) };
};

// ../src/hooks/useResizeObserver.ts
import * as React8 from "react";

// ../src/_global/resize_observer_polyfill.ts
import { ResizeObserver as ResizeObserverPolyfill } from "@juggle/resize-observer";
var polyfillResizeObserverGlobally = () => {
  if ("ResizeObserver" in globalThis === false) {
    globalThis.ResizeObserver = ResizeObserverPolyfill;
  }
};

// ../src/hooks/useResizeObserver.ts
polyfillResizeObserverGlobally();
var useResizeObserver = () => {
  const observer = React8.useRef();
  const rafId = React8.useRef();
  const [observerEntry, setObserverEntry] = React8.useState();
  const nodeRef = React8.useCallback((node) => {
    if (observer.current) {
      observer.current.disconnect();
      observer.current = void 0;
    }
    if (rafId.current) {
      window.cancelAnimationFrame(rafId.current);
      rafId.current = void 0;
    }
    if (node) {
      observer.current = new ResizeObserver((entries) => {
        if (rafId.current) {
          window.cancelAnimationFrame(rafId.current);
          rafId.current = void 0;
        }
        rafId.current = window.requestAnimationFrame(() => {
          rafId.current = void 0;
          if (!Array.isArray(entries) || !entries.length) {
            return;
          }
          setObserverEntry(entries[0]);
        });
      });
      observer.current.observe(node);
    }
  }, []);
  return { nodeRef, observerEntry };
};

// ../src/hooks/useFirstDefined.ts
import { useMemo } from "react";
function useFirstDefined(...args) {
  return useMemo(() => args.find((arg) => arg !== void 0), [args]);
}

// ../src/hooks/useBreakpoint.ts
import * as React9 from "react";
import { useMediaQuery } from "@react-hookz/web";

// ../../design-tokens/dist/dig/index.js
var BreakpointXSmall = "0";
var BreakpointSmall = "600";
var BreakpointMedium = "1024";
var BreakpointLarge = "1600";
var BreakpointXLarge = "1920";

// ../src/_global/breakpoints.ts
var breakpoints = {
  xsmall: parseInt(BreakpointXSmall, 10),
  small: parseInt(BreakpointSmall, 10),
  medium: parseInt(BreakpointMedium, 10),
  large: parseInt(BreakpointLarge, 10),
  xlarge: parseInt(BreakpointXLarge, 10)
};

// ../src/hooks/useBreakpoint.ts
var bpSizeMap = breakpoints;
var createMinWidthQuery = (minWidth) => `(min-width: ${minWidth}px)`;
var createMaxWidthQuery = (maxWidth) => `(max-width: ${maxWidth}px)`;
var createBetweenWidthQuery = (minWidth, maxWidth) => `${createMinWidthQuery(minWidth)} ${maxWidth && `and ${createMaxWidthQuery(maxWidth)}`}`;
var mqStrategyFactory = (strategy) => {
  let mq;
  switch (strategy) {
    case "maxWidth":
      mq = createMaxWidthQuery;
      break;
    case "betweenWidth":
      mq = createBetweenWidthQuery;
      break;
    case "minWidth":
    default:
      mq = createMinWidthQuery;
      break;
  }
  return mq;
};
var useBreakpoint = (props = {}) => {
  const { strategy = "minWidth", breakpoints: breakpointsProps } = props;
  const breakpoints2 = { ...breakpoints, ...breakpointsProps };
  const mqStrategy = React9.useMemo(() => mqStrategyFactory(strategy), [strategy]);
  const xsmall = useMediaQuery(mqStrategy(breakpoints2.xsmall, breakpoints2.small - 1));
  const bp = {
    xsmall: strategy === "minWidth" ? true : xsmall,
    small: useMediaQuery(mqStrategy(breakpoints2.small, breakpoints2.medium - 1)),
    medium: useMediaQuery(mqStrategy(breakpoints2.medium, breakpoints2.large - 1)),
    large: useMediaQuery(mqStrategy(breakpoints2.large, breakpoints2.xlarge - 1)),
    // No upper bound for xlarge since we have no other breakpoints
    xlarge: useMediaQuery(mqStrategy(breakpoints2.xlarge))
  };
  return bp;
};

// ../src/hooks/useResolveBreakpointProp.ts
var minWidthBpOrder = ["xlarge", "large", "medium", "small", "xsmall"];
function useResolveBreakpointProp(responsiveProp) {
  const activeMqs = useBreakpoint();
  if (typeof responsiveProp === "undefined") {
    return { value: void 0 };
  }
  const activeProp = minWidthBpOrder.filter((bp) => activeMqs[bp]).find((bp) => responsiveProp.hasOwnProperty(bp));
  const resolvedMqProp = (
    // eslint-disable-next-line no-prototype-builtins
    activeProp && responsiveProp.hasOwnProperty(activeProp) ? responsiveProp[activeProp] : (
      // If no active prop is found, return the `xsmall` value, which has to be defined.
      responsiveProp["xsmall"]
    )
  );
  return { value: resolvedMqProp };
}
export {
  areRowsFocused,
  bpSizeMap,
  createMenuRows,
  focusItem,
  getFocusableRows,
  getNextFocusableItem,
  getPreviousFocusableItem,
  isAnchorElement,
  isButtonElement,
  isFocusingMenuRow,
  isKeyboardEvent,
  isMenuRowDisabled,
  isRowFirst,
  moveFocusBackward,
  moveFocusByString,
  moveFocusForward,
  moveFocusTo,
  returnFocus,
  useBreakpoint,
  useFirstDefined,
  useMaxHeight,
  useMenu,
  useMenuItem,
  useMenuRows,
  useResizeObserver,
  useResolveBreakpointProp,
  useSubmenu,
  useUniqueId
};
